import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Check, CloudOff, FileDown, Loader2, Sparkles } from 'lucide-react';
import { useEnregistrerFiche, useFicheSociale } from '@/api/social';
import {
  Bandeau,
  Case,
  Choix,
  FicheCtx,
  Ligne,
  OuiNon,
  PuceOuiNon,
  Question,
  Rubrique,
  Texte,
  TexteLibre,
  Zone,
} from './champs';
import { JOURS, manquants, normaliser, type FicheSalarie, type Jour } from './ficheSalarie';
import SignaturePad from './SignaturePad';
import FinaliserModal from './FinaliserModal';
import OptionInactive from './OptionInactive';
import { useCurrentUser } from '@/api/auth';

const SECTIONS: Array<[string, string]> = [
  ['salarie', 'Le salarié'],
  ['entreprise', 'Votre entreprise'],
  ['contrat', 'Le contrat'],
  ['emploi', "L'emploi"],
  ['temps', 'Temps de travail'],
  ['remuneration', 'Rémunération'],
  ['particuliers', 'Contrats particuliers'],
  ['signature', 'Signature'],
];

const JOUR_LABEL: Record<Jour, string> = {
  lundi: 'Lundi',
  mardi: 'Mardi',
  mercredi: 'Mercredi',
  jeudi: 'Jeudi',
  vendredi: 'Vendredi',
  samedi: 'Samedi',
  dimanche: 'Dimanche',
};

type Etat = 'enregistre' | 'modifie' | 'enregistrement' | 'erreur';

export default function SocialFormPage() {
  const { data: user } = useCurrentUser();
  if (!user?.options?.gestionSociale) return <OptionInactive />;
  return <Editeur />;
}

function Editeur() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fiche = useFicheSociale(id);
  const enregistrer = useEnregistrerFiche(id!);

  const [d, setD] = useState<FicheSalarie | null>(null);
  const [etat, setEtat] = useState<Etat>('enregistre');
  const [finaliser, setFinaliser] = useState(false);
  const [alerte, setAlerte] = useState<string | null>(null);

  // La fiche chargée devient l'état local — une seule fois.
  useEffect(() => {
    if (fiche.data && !d) setD(normaliser(fiche.data.data));
  }, [fiche.data, d]);

  // --- Enregistrement automatique -----------------------------------------
  // Quatre pages de questions : perdre la saisie sur un rechargement serait
  // rédhibitoire. On enregistre 800 ms après la dernière frappe.
  const derniere = useRef<FicheSalarie | null>(null);
  const minuteur = useRef<number | null>(null);

  const flush = useCallback(async () => {
    if (minuteur.current) {
      window.clearTimeout(minuteur.current);
      minuteur.current = null;
    }
    const aEnvoyer = derniere.current;
    if (!aEnvoyer) return;
    derniere.current = null;
    setEtat('enregistrement');
    try {
      await enregistrer.mutateAsync(aEnvoyer);
      // Une frappe pendant l'envoi a relancé un minuteur : on reste « modifié ».
      setEtat(derniere.current ? 'modifie' : 'enregistre');
    } catch {
      derniere.current ??= aEnvoyer;
      setEtat('erreur');
      throw new Error('Enregistrement impossible');
    }
  }, [enregistrer]);

  const set = useCallback(<K extends keyof FicheSalarie>(k: K, v: FicheSalarie[K]) => {
    setD((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [k]: v };
      derniere.current = next;
      return next;
    });
    setEtat('modifie');
    setAlerte(null);
    if (minuteur.current) window.clearTimeout(minuteur.current);
    minuteur.current = window.setTimeout(() => {
      flush().catch(() => undefined);
    }, 800);
  }, [flush]);

  // En quittant la page (lien, retour) : on n'abandonne pas la dernière frappe.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => () => void flushRef.current().catch(() => undefined), []);

  // Fermeture de l'onglet avec des modifications non enregistrées.
  useEffect(() => {
    const avant = (e: BeforeUnloadEvent) => {
      if (derniere.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', avant);
    return () => window.removeEventListener('beforeunload', avant);
  }, []);

  const ctx = useMemo(() => (d ? { d, set } : null), [d, set]);

  const terminer = async () => {
    if (!d) return;
    const m = manquants(d);
    if (m.length) {
      setAlerte(`À renseigner avant de générer le PDF : ${m.join(', ')}.`);
      document.getElementById('salarie')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    try {
      await flush();
    } catch {
      setAlerte("La fiche n'a pas pu être enregistrée. Vérifiez votre connexion et réessayez.");
      return;
    }
    setFinaliser(true);
  };

  if (fiche.isError) {
    return (
      <div className="p-8">
        <div className="max-w-xl rounded-xl border border-danger/30 bg-danger-bg p-4 text-[13px] text-danger flex items-center gap-2">
          <AlertCircle size={15} /> {(fiche.error as Error).message}
        </div>
        <Link to="/gestion-sociale" className="inline-block mt-4 text-[13px] text-slate-600 hover:text-slate-900">
          ← Retour aux fiches
        </Link>
      </div>
    );
  }
  if (!d || !ctx) {
    return <div className="p-8 text-[13px] text-slate-500">Chargement de la fiche…</div>;
  }

  const nomAffiche = [d.prenom, d.nom.toUpperCase()].filter((s) => s.trim()).join(' ') || 'Nouveau salarié';
  const periodes = d.periodes;
  const setPeriode = (i: number, champ: keyof FicheSalarie['periodes'][number], v: string) =>
    set('periodes', periodes.map((p, j) => (j === i ? { ...p, [champ]: v } : p)));
  const setJour = (j: Jour, champ: 'heures' | 'horaires', v: string) =>
    set('repartitionSemaine', { ...d.repartitionSemaine, [j]: { ...d.repartitionSemaine[j], [champ]: v } });
  const setSemaine = (i: number, champ: 'heures' | 'horaires', v: string) =>
    set('repartitionMois', d.repartitionMois.map((s, j) => (j === i ? { ...s, [champ]: v } : s)));

  return (
    <FicheCtx.Provider value={ctx}>
      <div className="flex flex-col h-full">
        {/* En-tête */}
        <div className="border-b border-slate-200 bg-white px-8 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/gestion-sociale')}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"
              title="Retour aux fiches"
            >
              <ArrowLeft size={17} />
            </button>
            <div className="min-w-0">
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-emerald-600">
                Gestion Sociale · Fiche d’identification du salarié
              </div>
              <h1 className="text-[19px] font-semibold tracking-tight text-slate-900 truncate">{nomAffiche}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <EtatEnregistrement etat={etat} />
            <button
              onClick={terminer}
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-[13px] font-semibold text-white bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 shadow-md shadow-emerald-500/25 hover:brightness-110 transition"
            >
              <FileDown size={15} /> Terminer et générer le PDF
            </button>
          </div>
        </div>

        {/* Sommaire */}
        <div className="border-b border-slate-200 bg-white/80 px-8 py-2 flex gap-1 overflow-x-auto">
          {SECTIONS.map(([ancre, label]) => (
            <button
              key={ancre}
              onClick={() => document.getElementById(ancre)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="px-2.5 py-1 rounded-full text-[12px] text-slate-600 hover:bg-emerald-50 hover:text-emerald-800 whitespace-nowrap"
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto bg-slate-100 px-6 py-6">
          <div className="max-w-4xl mx-auto space-y-5">
            {alerte && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-3.5 text-[13px] text-amber-900 flex items-center gap-2">
                <AlertCircle size={15} className="flex-shrink-0" /> {alerte}
              </div>
            )}

            {/* Nom de l'entreprise + rappel légal */}
            <div className="rounded-xl bg-white border border-slate-200 p-5 space-y-3">
              <Texte k="entreprise" label={<b className="text-emerald-700">NOM DE L’ENTREPRISE :</b>} />
              <p className="text-[12px] leading-relaxed text-slate-600">
                Nous vous rappelons que le contrat de travail doit, en principe, être remis le jour de l’embauche et
                dans le cadre d’un CDD, au plus tard dans les 48 heures qui lui succèdent. Afin de respecter cette
                obligation légale et conformément à la lettre de mission qui nous lie, vous devez nous retourner ce
                document dûment complété <b>5 jours ouvrés avant la date d’embauche</b>. A défaut, nous ne pourrons
                garantir le respect des délais.
              </p>
            </div>

            {/* ============================ LE SALARIÉ ============================ */}
            <Carte>
              <Bandeau id="salarie">Le salarié</Bandeau>
              <Rubrique titre="Identité">
                <Ligne>
                  <Texte k="nom" label="Nom :" />
                  <Texte k="prenom" label="Prénom :" />
                </Ligne>
                <Texte k="nomJeuneFille" label="Nom de jeune fille (le cas échéant) :" />
                <Ligne>
                  <Texte k="dateNaissance" type="date" label="Né(e) le" className="w-[250px]" />
                  <Texte k="lieuNaissance" label="à (lieu de naissance)" />
                </Ligne>
                <Ligne>
                  <Question>Nationalité :</Question>
                  <Choix k="nationalite" options={[['FRANCAISE', 'Française'], ['AUTRE', 'Autre :']]} />
                  <Texte k="nationaliteAutre" placeholder="Précisez la nationalité" />
                </Ligne>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1">
                  <Texte
                    k="titreSejour"
                    label={<b>Si salarié étranger (hors UE), précisez le n° du titre de séjour/de travail :</b>}
                  />
                  <p className="text-[12px] font-semibold italic text-slate-600">
                    Dans ce cas, merci de nous en retourner une copie.
                  </p>
                </div>
                <Ligne>
                  <Texte k="numeroSecu" label="N° de sécurité sociale :" inputMode="numeric" placeholder="1 23 45 67 890 123 45" />
                  <Texte k="mail" type="email" label="Mail :" />
                </Ligne>
              </Rubrique>

              <Rubrique titre="Situation personnelle">
                <Ligne>
                  <Choix
                    k="situation"
                    options={[
                      ['MARIE', 'Marié(e)'],
                      ['PACSE', 'Pacsé(e)'],
                      ['CELIBATAIRE', 'Célibataire'],
                      ['DIVORCE', 'Divorcé(e)'],
                      ['CONCUBINAGE', 'En concubinage'],
                      ['AUTRE', 'Autre :'],
                    ]}
                  />
                  <Texte k="situationAutre" className="w-48" />
                </Ligne>
              </Rubrique>

              <Rubrique titre="Localisation">
                <Texte k="adresse" label="Adresse :" />
                <Ligne>
                  <Texte k="codePostal" label="Code postal :" inputMode="numeric" className="w-56" />
                  <Texte k="ville" label="Ville :" />
                </Ligne>
              </Rubrique>

              <Rubrique>
                <Ligne>
                  <Question><b>Le salarié a-t-il déjà été employé au sein de votre entreprise ?</b></Question>
                  <OuiNon k="dejaEmploye" ordre="non-oui" />
                </Ligne>
                <p className="text-[13px] text-slate-700">
                  Si oui, indiquez le type de contrat et les dates de présence du salarié dans votre entreprise ci-après.
                </p>
                <div className={`space-y-3 ${d.dejaEmploye === 'NON' ? 'opacity-50' : ''}`}>
                  {periodes.map((p, i) => (
                    <div key={i} className="pl-4 border-l-2 border-emerald-200 space-y-2">
                      <Ligne>
                        <TexteLibre type="date" label="▪ Du" value={p.du} onChange={(v) => setPeriode(i, 'du', v)} className="w-[230px]" />
                        <TexteLibre type="date" label="Au" value={p.au} onChange={(v) => setPeriode(i, 'au', v)} className="w-[210px]" />
                      </Ligne>
                      <Ligne>
                        {([['INTERIM', 'Intérim'], ['CDD', 'CDD'], ['CDI', 'CDI'], ['PARTICULIER', 'Contrat particulier (si oui, précisez) :']] as const).map(
                          ([val, label]) => (
                            <Case key={val} on={p.type === val} onClick={() => setPeriode(i, 'type', p.type === val ? '' : val)}>
                              {label}
                            </Case>
                          ),
                        )}
                        <TexteLibre value={p.precision} onChange={(v) => setPeriode(i, 'precision', v)} className="w-48" />
                      </Ligne>
                    </div>
                  ))}
                </div>
              </Rubrique>

              <Rubrique>
                <Ligne>
                  <Question><b>Existe-t-il un lien de parenté entre le salarié et l’employeur ?</b></Question>
                  <Choix k="lienParente" options={[['NON', 'Non'], ['OUI', 'Oui (précisez)']]} />
                  <Texte k="lienParentePrecision" className="w-56" />
                </Ligne>
                <Ligne>
                  <Question><b>Le salarié est-il actionnaire, associé ou administrateur dans l’entreprise ?</b></Question>
                  <OuiNon k="actionnaire" ordre="non-oui" />
                </Ligne>
              </Rubrique>

              <Rubrique titre="Situation professionnelle antérieure à l’embauche">
                <Ligne>
                  <Choix
                    k="situationAnterieure"
                    options={[['CHOMAGE', 'Chômage'], ['ETUDES', 'Etudes'], ['CDD', 'CDD'], ['CDI', 'CDI'], ['AUTRE', 'Autre (précisez) :']]}
                  />
                  <Texte k="situationAnterieureAutre" className="w-56" />
                </Ligne>
              </Rubrique>

              <Rubrique titre="Diplômes, Formations et Expérience">
                <Texte k="dernierDiplome" label="Dernier diplôme obtenu (CAP, BP, BAC PRO, BTS, Licence, Master etc.) :" />
                <Texte k="cqp" label="CQP obtenus :" />
                <div>
                  <div className="text-[13px] text-slate-700 mb-1">Expérience :</div>
                  <Zone k="experience" lignes={2} />
                </div>
              </Rubrique>
            </Carte>

            {/* ========================= VOTRE ENTREPRISE ========================= */}
            <Carte>
              <Bandeau id="entreprise">Votre entreprise</Bandeau>
              <Rubrique>
                <Ligne>
                  <Question>Adhérez-vous à une organisation patronale ?</Question>
                  <Choix k="organisationPatronale" options={[['NON', 'Non'], ['OUI', 'Oui (si oui, précisez)']]} />
                  <Texte k="organisationPatronalePrecision" className="w-60" />
                </Ligne>
                <Ligne>
                  <Question>Avez-vous déjà licencié pour motif économique ?</Question>
                  <Choix k="licenciementEco" options={[['NON', 'Non'], ['OUI', 'Oui (dates)']]} />
                  <Texte k="licenciementEcoDates" className="w-60" />
                </Ligne>
                <Texte k="conventionCollective" label="Convention collective appliquée ?" />
              </Rubrique>
            </Carte>

            {/* ======================== LE CONTRAT DE TRAVAIL ======================= */}
            <Carte>
              <Bandeau id="contrat">Le contrat de travail</Bandeau>
              <div className="bg-emerald-50 border-b border-emerald-100 px-5 py-3 flex items-center justify-center gap-3">
                <span className="text-[15px] font-bold tracking-wide text-emerald-900">DATE D’EMBAUCHE :</span>
                <Texte k="dateEmbauche" type="date" className="w-44" />
              </div>
              <Rubrique>
                <Choix
                  k="typeContrat"
                  fort
                  vertical
                  options={[['CDI', 'CDI (Contrat à Durée Indéterminée)'], ['CDD', 'CDD (Contrat à Durée Déterminée)']]}
                />
                <div className={`pl-6 space-y-4 transition-opacity ${d.typeContrat === 'CDI' ? 'opacity-40' : ''}`}>
                  <div className="text-[13px] font-semibold text-slate-800">Dans ce cas, compléter les cases ci-après</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Choix k="cddTerme" options={[['PRECIS', <b key="p">Terme précis</b>]]} />
                      <Texte k="cddDateFin" type="date" label={<b><u>Date de fin</u> :</b>} className="w-[250px]" />
                    </div>
                    <div className="space-y-2">
                      <Choix k="cddTerme" options={[['IMPRECIS', <b key="i">Terme imprécis</b>]]} />
                      <Texte k="cddDureeMinimale" label={<b>Durée minimale :</b>} />
                      <Texte k="cddTermeTexte" label={<b>Terme :</b>} />
                    </div>
                  </div>

                  <div className="pl-4 space-y-4">
                    <div className="space-y-2">
                      <Choix k="cddMotif" options={[['REMPLACEMENT', <b key="r">Remplacement d’un salarié</b>]]} />
                      <div className="pl-6 space-y-2">
                        <Choix k="remplacementType" options={[['TOTAL', 'Total'], ['PARTIEL', 'Partiel']]} />
                        <Texte k="remplacementNom" label="Nom du salarié absent :" />
                        <Ligne>
                          <Question>Motif d’absence :</Question>
                          <Choix
                            k="remplacementMotif"
                            options={[
                              ['MALADIE', 'Maladie*'],
                              ['HOSPITALISATION', 'Hospitalisation*'],
                              ['CONGES', 'Congés-payés'],
                              ['FORMATION', 'Formation'],
                              ['AUTRE', 'Autre (précisez)'],
                            ]}
                          />
                          <Texte k="remplacementMotifAutre" className="w-52" />
                        </Ligne>
                        <p className="text-[12px] font-semibold italic text-slate-600">
                          * Merci de nous retourner le justificatif d’absence (arrêt de travail, bulletin de situation)
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Choix k="cddMotif" options={[['ACCROISSEMENT', <b key="a">Accroissement temporaire d’activité</b>]]} />
                      <div className="pl-6">
                        <div className="text-[13px] text-slate-700 mb-1">
                          <b>Motif <u>précis</u></b> (justifiant l’accroissement) :
                        </div>
                        <Zone k="accroissementMotif" lignes={3} />
                      </div>
                    </div>

                    <Ligne>
                      <Choix k="cddMotif" options={[['SAISONNIER', <b key="s">Saisonnier</b>]]} />
                      <Texte k="saison" label="- Précisez la saison :" />
                    </Ligne>
                    <Ligne>
                      <Choix k="cddMotif" options={[['AUTRE', <b key="o">Autre (usage, extra etc.)</b>]]} />
                      <Texte k="cddAutre" label="- Précisez :" />
                    </Ligne>
                  </div>
                </div>
                {d.typeContrat === 'CDI' && (
                  <p className="text-[11.5px] text-slate-500 pl-6">Partie CDD : sans objet pour un CDI.</p>
                )}
              </Rubrique>

              <Rubrique>
                <Ligne>
                  <Question><b>Période d’essai</b></Question>
                  <OuiNon k="periodeEssai" ordre="non-oui" />
                </Ligne>
                <Ligne>
                  <Question><b>Si oui, quelle durée envisagez-vous ?</b></Question>
                  <Choix k="essaiDuree" options={[['MAX', 'Durée maximale autorisée (loi/CCN)'], ['AUTRE', 'Autre (précisez) :']]} />
                  <Texte k="essaiDureeAutre" className="w-48" />
                </Ligne>
                <Ligne>
                  <Question><b>Voulez-vous prévoir une possibilité de renouvellement ?</b></Question>
                  <OuiNon k="essaiRenouvellement" />
                </Ligne>
              </Rubrique>
            </Carte>

            {/* ============================== L'EMPLOI ============================== */}
            <Carte>
              <Bandeau id="emploi">L’emploi</Bandeau>
              <Rubrique titre="Classification du poste">
                <Texte k="intitulePoste" label="Intitulé du poste :" />
                <Ligne>
                  <Texte k="niveau" label="Niveau :" />
                  <Texte k="echelon" label="Échelon :" />
                  <Texte k="coefficient" label="Coefficient :" />
                </Ligne>
                <Ligne>
                  <Question>Statut :</Question>
                  <Choix k="statut" options={[['CADRE', 'Cadre'], ['NON_CADRE', 'Non cadre']]} />
                  <span className="text-[13px] text-slate-600">(si non cadre :</span>
                  <Choix
                    k="categorie"
                    options={[['OUVRIER', 'Ouvrier'], ['EMPLOYE', 'Employé(e)'], ['TECHNICIEN', 'Technicien'], ['AGENT_MAITRISE', 'Agent de maîtrise']]}
                  />
                  <span className="text-[13px] text-slate-600">)</span>
                </Ligne>
              </Rubrique>
              <Rubrique
                titre="Tâches exercées par le/la salarié(e)"
                note="Merci de préciser en détails, les tâches attribuées au salarié"
              >
                <Zone k="taches" lignes={4} />
              </Rubrique>
              <Rubrique titre="Lieu de travail">
                <Zone k="lieuTravail" lignes={2} />
                <Ligne>
                  <Question>Souhaitez-vous prévoir la possibilité pour le salarié d’effectuer des déplacements ?</Question>
                  <OuiNon k="deplacements" />
                </Ligne>
                <Ligne>
                  <Question>Si oui, voulez-vous préciser des limites (géographiques, d’établissements, etc) ?</Question>
                  <OuiNon k="deplacementsLimites" />
                </Ligne>
                <Texte k="deplacementsLesquelles" label="Lesquelles ?" />
              </Rubrique>
            </Carte>

            {/* =========================== TEMPS DE TRAVAIL ========================== */}
            <Carte>
              <Bandeau id="temps">Temps de travail</Bandeau>
              <Rubrique>
                <Ligne>
                  <Question>Durée de travail hebdomadaire :</Question>
                  <Choix k="dureeHebdo" options={[['35H', '35 heures'], ['AUTRE', 'Autre (précisez)']]} />
                  <Texte k="dureeHebdoAutre" className="w-48" />
                </Ligne>
                <p className="text-[12px] italic leading-relaxed text-slate-600">
                  Dans le cadre d’un temps partiel, la durée de travail <b>ne peut être inférieure à 24 heures</b> (durée
                  minimale de travail fixée par la loi), sauf en cas de demande expresse du salarié à travailler en
                  dessous de cette limite (pour contraintes personnelles, pour cumul de plusieurs activités, pour étudier
                  s’il est âgé de moins de 26 ans), si un accord de branche étendu prévoit cette possibilité ou en
                  application d’autres dérogations de droit en vigueur.
                </p>
                <p className="text-[12.5px] font-bold italic text-slate-700">
                  Si tel est le cas, merci de nous envoyer le courrier du salarié, motivant sa demande.
                </p>
                <div className="text-[13px] text-slate-800">Le salarié va-t-il être amené à travailler :</div>
                <PuceOuiNon k="dimanches">Les dimanches ?</PuceOuiNon>
                <PuceOuiNon k="nuits">Les nuits ?</PuceOuiNon>
                <PuceOuiNon k="feries">Les jours fériés ?</PuceOuiNon>
              </Rubrique>

              <div className="grid md:grid-cols-2 border-t border-slate-200">
                {/* Temps complet */}
                <div className="md:border-r border-slate-200">
                  <SousBandeau>Temps complet (35 heures ou plus)</SousBandeau>
                  <div className="p-4 space-y-3">
                    <p className="text-[12.5px] font-semibold text-center text-slate-800">
                      Si le salarié est embauché à temps <u>COMPLET</u>, merci de bien vouloir cocher les cases suivantes
                    </p>
                    <div className="text-[13px] text-slate-800">Selon quel horaire le salarié est-il recruté ?</div>
                    <div className="pl-4"><Choix k="horaire" vertical options={[['COLLECTIF', 'Collectif'], ['INDIVIDUEL', 'Individuel']]} /></div>
                    <div className="text-[13px] text-slate-800">Nombre de jours travaillés dans la semaine :</div>
                    <div className="pl-4 space-y-1.5">
                      <Choix k="joursSemaine" vertical options={[['4', '4 jours'], ['5', '5 jours'], ['6', '6 jours'], ['AUTRE', 'Autre (précisez)']]} />
                      <Texte k="joursSemaineAutre" className="w-full" />
                    </div>
                    <p className="text-[13px] text-slate-800">
                      Si le salarié est embauché dans le cadre d’un mode d’aménagement du temps de travail supérieur à
                      la semaine, complétez les cases ci-après :
                    </p>
                    <div className="pl-4 space-y-1.5">
                      <Choix k="amenagement" vertical options={[['MODULATION', 'Modulation (précisez)'], ['CYCLES', 'Cycles (précisez)'], ['FORFAIT', 'Forfait']]} />
                      <div className="pl-6">
                        <Choix
                          k="forfait"
                          vertical
                          options={[
                            ['ANNUEL_JOURS', 'Forfait annuel en jours'],
                            ['ANNUEL_HEURES', 'Forfait annuel en heures'],
                            ['MENSUEL_HEURES', 'Forfait mensuel en heures'],
                            ['HEBDO_HEURES', 'Forfait hebdomadaire en heures'],
                          ]}
                        />
                      </div>
                      <Choix k="amenagement" options={[['AUTRE', 'Autre (précisez)']]} />
                      <Texte k="amenagementPrecision" placeholder="Précision (modulation, cycles ou autre)" className="w-full" />
                    </div>
                  </div>
                </div>

                {/* Temps partiel */}
                <div className="border-t md:border-t-0 border-slate-200">
                  <SousBandeau>Temps partiel (&lt; 35 heures)</SousBandeau>
                  <p className="p-4 pb-2 text-[12.5px] font-semibold text-center text-slate-800">
                    Si le salarié est embauché à temps <u>PARTIEL</u>, merci de bien vouloir compléter les rubriques ci-après
                  </p>
                  <SousBandeau clair>Si répartition sur la semaine</SousBandeau>
                  <div className="p-4 space-y-1.5">
                    {JOURS.map((j) => (
                      <div key={j} className="grid grid-cols-[72px_78px_auto_1fr] items-center gap-2">
                        <span className="text-[13px] text-slate-700">{JOUR_LABEL[j]} :</span>
                        <TexteLibre value={d.repartitionSemaine[j].heures} onChange={(v) => setJour(j, 'heures', v)} className="" placeholder="0" />
                        <span className="text-[12px] text-slate-500">heures</span>
                        <TexteLibre value={d.repartitionSemaine[j].horaires} onChange={(v) => setJour(j, 'horaires', v)} className="" placeholder="ex. 9h-12h / 14h-19h" />
                      </div>
                    ))}
                  </div>
                  <SousBandeau clair>Si répartition sur le mois</SousBandeau>
                  <div className="p-4 space-y-1.5">
                    {d.repartitionMois.map((s, i) => (
                      <div key={i} className="grid grid-cols-[92px_78px_auto_1fr] items-center gap-2">
                        <span className="text-[13px] text-slate-700">Semaine n°{i + 1} :</span>
                        <TexteLibre value={s.heures} onChange={(v) => setSemaine(i, 'heures', v)} className="" placeholder="0" />
                        <span className="text-[12px] text-slate-500">heures</span>
                        <TexteLibre value={s.horaires} onChange={(v) => setSemaine(i, 'horaires', v)} className="" />
                      </div>
                    ))}
                    <p className="text-[13px] text-slate-800 pt-2">
                      Indiquez les cas éventuels de modification de la répartition des horaires (entre les jours de la
                      semaine ou les semaines du mois) :
                    </p>
                    <Zone k="modificationRepartition" lignes={2} />
                    <div className="text-[13px] text-slate-800 pt-1">Possibilité d’effectuer des heures complémentaires ?</div>
                    <OuiNon k="heuresComplementaires" />
                  </div>
                  <SousBandeau clair>Si répartition sur l’année</SousBandeau>
                  <div className="p-4 flex justify-center">
                    <Choix k="repartitionAnnee" options={[['MODULE', 'Partiel modulé'], ['INTERMITTENT', 'Travail intermittent']]} />
                  </div>
                </div>
              </div>
            </Carte>

            {/* ============================ RÉMUNÉRATION ============================ */}
            <Carte>
              <Bandeau id="remuneration">Rémunération</Bandeau>
              <div className="bg-emerald-50 border-b border-emerald-100 px-5 py-3 flex items-center justify-center">
                <Texte
                  k="salaireBrut"
                  label={<b className="text-emerald-900">Salaire brut mensuel du salarié</b>}
                  suffixe="€"
                  inputMode="decimal"
                  className="w-[420px]"
                />
              </div>
              <Rubrique>
                <Ligne>
                  <Question>Prime(s) :</Question>
                  <OuiNon k="primes" />
                </Ligne>
                <div>
                  <div className="text-[13px] text-slate-700 mb-1">Si oui, précisez lesquelles et leurs modalités d’octroi :</div>
                  <Zone k="primesPrecision" lignes={2} />
                </div>
                <div className="text-[13px] text-slate-800">Avantages fournis gratuitement ou à prix modique :</div>
                <PuceOuiNon k="nourriture">Nourriture :</PuceOuiNon>
                <PuceOuiNon k="logement">Logement :</PuceOuiNon>
                <PuceOuiNon k="vehiculeFonction" apres={<>(à usage professionnel <u>et</u> personnel)</>}>Véhicule de fonction :</PuceOuiNon>
                <PuceOuiNon k="vehiculeService" apres="(à usage professionnel, pour les besoins de l’activité)">Véhicule de service :</PuceOuiNon>
                <PuceOuiNon k="ordinateur">Ordinateur :</PuceOuiNon>
                <PuceOuiNon k="telephone">Téléphone portable :</PuceOuiNon>
                <Ligne className="pl-4">
                  <span className="text-slate-400">▪</span>
                  <Texte k="avantageAutre" label="Autre (précisez) :" />
                </Ligne>
                <Ligne>
                  <Question>Le salarié est-il amené à engager des frais professionnels, dans le cadre de ses fonctions ?</Question>
                  <OuiNon k="fraisPro" />
                </Ligne>
                <Texte k="fraisProLesquels" label="Lesquels ?" />
                <Ligne>
                  <Question>Pour les secteurs concernés (BTP, VRP etc.), pratiquez-vous l’abattement pour frais professionnels ?</Question>
                  <OuiNon k="abattement" />
                </Ligne>
              </Rubrique>
              <Rubrique titre="Congés-payés">
                <Ligne>
                  <Question>Régime de congés-payés :</Question>
                  <Choix k="congesRegime" options={[['GENERAL', 'Régime général'], ['CAISSE', 'Caisse de Congés-payés']]} />
                </Ligne>
                <Ligne>
                  <Question>Décompte :</Question>
                  <Choix k="congesDecompte" options={[['OUVRES', 'Jours ouvrés (25 jours/an)'], ['OUVRABLES', 'Jours ouvrables (30 jours/an)']]} />
                </Ligne>
              </Rubrique>
              <Rubrique>
                <Ligne>
                  <Question><b>Clauses particulières à insérer au contrat</b></Question>
                  <OuiNon k="clauses" />
                </Ligne>
                <div>
                  <div className="text-[13px] text-slate-700 mb-1">Si oui, précisez :</div>
                  <Zone k="clausesPrecision" lignes={3} />
                  <p className="text-[11.5px] italic text-slate-500 mt-1">
                    Exemples : déontologie, mise à disposition d’un véhicule à usage professionnel, confidentialité,
                    clause de non-concurrence etc.
                  </p>
                </div>
              </Rubrique>
            </Carte>

            {/* ========================= CONTRATS PARTICULIERS ======================== */}
            <Carte>
              <Bandeau id="particuliers">Contrats particuliers</Bandeau>
              <Rubrique>
                <div className="flex flex-col items-center gap-2">
                  <Choix
                    k="contratParticulier"
                    vertical
                    options={[
                      ['APPRENTISSAGE', 'Contrat d’apprentissage'],
                      ['PROFESSIONNALISATION', 'Contrat de professionnalisation'],
                      ['AUTRE', 'Autre (précisez)'],
                    ]}
                  />
                  <Texte k="contratParticulierAutre" className="w-80" />
                </div>
              </Rubrique>
            </Carte>

            {/* ============================== SIGNATURE ============================== */}
            <Carte>
              <Bandeau id="signature">Date et signature</Bandeau>
              <div className="grid md:grid-cols-2 gap-6 p-5">
                <div className="space-y-3">
                  <Texte k="signataire" label="Nom du signataire :" />
                  <Texte k="dateSignature" type="date" label="Date :" className="w-[230px]" />
                  <SignaturePad value={d.signature} onChange={(png) => set('signature', png)} />
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-slate-800 mb-2">Cachet de l’entreprise :</div>
                  <div className="h-36 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-center px-6 text-[12px] text-slate-400">
                    Emplacement laissé libre sur le PDF, pour le tampon si le cabinet le demande.
                  </div>
                </div>
              </div>
            </Carte>

            <div className="flex justify-end pb-10">
              <button
                onClick={terminer}
                className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-[14px] font-semibold text-white bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 shadow-lg shadow-emerald-500/25 hover:brightness-110 transition"
              >
                <Sparkles size={16} /> Terminer et générer le PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {finaliser && id && <FinaliserModal id={id} fiche={d} onClose={() => setFinaliser(false)} />}
    </FicheCtx.Provider>
  );
}

function Carte({ children }: { children: React.ReactNode }) {
  return <section className="rounded-xl bg-white border border-slate-200 overflow-hidden shadow-sm">{children}</section>;
}

function SousBandeau({ children, clair }: { children: React.ReactNode; clair?: boolean }) {
  return (
    <div
      className={`px-4 py-2 text-center text-[12.5px] font-semibold ${
        clair ? 'bg-emerald-100 text-emerald-900' : 'bg-teal-600 text-white'
      }`}
    >
      {children}
    </div>
  );
}

function EtatEnregistrement({ etat }: { etat: Etat }) {
  if (etat === 'enregistrement' || etat === 'modifie') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-500">
        <Loader2 size={13} className="animate-spin" /> Enregistrement…
      </span>
    );
  }
  if (etat === 'erreur') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12px] text-danger">
        <CloudOff size={13} /> Non enregistré
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-emerald-700">
      <Check size={13} /> Enregistré
    </span>
  );
}
